import React, { useState } from 'react';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useGetBookingEventReportQuery } from '../Service/bookingEventReportApi';
import {
  formatBookingCurrency,
  formatBookingExchangeRate,
  formatRevenueAmountPln,
  hasForeignRevenue,
} from '../Helpers/formatBookingCurrency';
import {
  createBookingEventReportPdfBlob,
  downloadBookingEventReportPdf,
} from '../Helpers/bookingEventReportPdfExport';
import type { BookingEventReportDto } from '../Types/bookingEventReport.types';
import dayjs from 'dayjs';

interface BookingEventReportPanelProps {
  bookingId: number;
}

const formatDate = (value: string): string => {
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD.MM.YYYY') : value;
};

const hasExchangeRates = (data: BookingEventReportDto): boolean =>
  data.expenseGroups.some((g) => g.items.some((i) => i.exchangeRate !== null));

const BookingEventReportPanel: React.FC<BookingEventReportPanelProps> = ({ bookingId }) => {
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const { data, isLoading, isError } = useGetBookingEventReportQuery(bookingId, {
    skip: !Number.isFinite(bookingId) || bookingId <= 0,
  });

  const handleDownloadPdf = async () => {
    if (!data) return;
    setPdfError(null);
    setIsExportingPdf(true);
    try {
      const blob = await createBookingEventReportPdfBlob(data);
      downloadBookingEventReportPdf(blob, `rozliczenie-imprezy-${bookingId}.pdf`);
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'Nie udało się wygenerować PDF rozliczenia imprezy.';
      setPdfError(message);
    } finally {
      setIsExportingPdf(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Alert severity="error">
        Nie udało się pobrać rozliczenia imprezy.
      </Alert>
    );
  }

  if (data.expenseGroups.length === 0 && data.revenueItems.length === 0) {
    return (
      <Alert severity="info">
        Brak danych do wyświetlenia rozliczenia imprezy.
      </Alert>
    );
  }

  const showExchangeRate = hasExchangeRates(data);
  const showRevenueFx = hasForeignRevenue(data.revenueItems);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {data.offerName ?? 'Bez nazwy'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {formatDate(data.bookingDateFrom)} – {formatDate(data.bookingDateTo)}
        </Typography>
      </Box>

      {/* PDF button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={isExportingPdf ? <CircularProgress size={14} /> : <PictureAsPdfIcon />}
          onClick={handleDownloadPdf}
          disabled={isExportingPdf}
        >
          {isExportingPdf ? 'Generowanie...' : 'Pobierz PDF'}
        </Button>
      </Box>

      {pdfError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {pdfError}
        </Alert>
      )}

      {/* Expense table */}
      {data.expenseGroups.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Koszty</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Opis dokumentu</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Numer faktury</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Info</TableCell>
                {showExchangeRate && (
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Kurs
                  </TableCell>
                )}
                <TableCell sx={{ fontWeight: 600 }} align="right">Razem</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">W PLN</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.expenseGroups.map((group) => (
                <React.Fragment key={group.expenseType}>
                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                    <TableCell
                      colSpan={showExchangeRate ? 7 : 6}
                      sx={{
                        fontWeight: 700,
                        py: 1,
                        letterSpacing: 1,
                        textTransform: 'uppercase',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      ❯ {group.expenseType}
                    </TableCell>
                  </TableRow>
                  {group.items.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell />
                      <TableCell>{item.issuer ?? '—'}</TableCell>
                      <TableCell>{item.documentNumber ?? '—'}</TableCell>
                      <TableCell>{item.paymentMethod ?? '—'}</TableCell>
                      {showExchangeRate && (
                        <TableCell align="right">
                          {item.exchangeRate != null ? item.exchangeRate : '—'}
                        </TableCell>
                      )}
                      <TableCell align="right">
                        {item.total.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                        {item.currency ?? 'PLN'}
                      </TableCell>
                      <TableCell align="right">{formatBookingCurrency(item.totalPln)}</TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mt: 1, gap: 0.5 }}>
            <Typography variant="body2" fontWeight={600}>
              Koszty razem: {formatBookingCurrency(data.totalCosts)}
            </Typography>
            {data.expenseGroups
              .filter((g) => g.cashBreakdown.length > 0)
              .flatMap((g) =>
                g.cashBreakdown.map((b) => {
                  const isForeign = b.currency !== 'PLN';
                  const originalFormatted = `${b.totalOriginal.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${b.currency}`;
                  const label = isForeign ? `${originalFormatted} (${formatBookingCurrency(b.totalPln)})` : originalFormatted;
                  return (
                    <Typography key={`${g.expenseType}-${b.currency}`} variant="body2" color="text.secondary">
                      w tym {g.expenseType} gotówką: {label}
                    </Typography>
                  );
                })
              )}
          </Box>
        </Box>
      )}

      {/* Revenue table */}
      {data.revenueItems.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: 48 }}>Lp</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Nr faktury</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Nabywca</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Adres</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>NIP</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Kwota</TableCell>
                {showRevenueFx && (
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Kurs
                  </TableCell>
                )}
                {showRevenueFx && (
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    W PLN
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.revenueItems.map((item) => (
                <TableRow key={item.lp}>
                  <TableCell>{item.lp}</TableCell>
                  <TableCell>{item.invoiceNumber}</TableCell>
                  <TableCell>{item.buyer}</TableCell>
                  <TableCell>{item.buyerAddress ?? '—'}</TableCell>
                  <TableCell>{item.buyerNip ?? '—'}</TableCell>
                  <TableCell align="right">
                    {formatBookingCurrency(item.amount, item.currency)}
                  </TableCell>
                  {showRevenueFx && (
                    <TableCell align="right">
                      {formatBookingExchangeRate(item.exchangeRate)}
                    </TableCell>
                  )}
                  {showRevenueFx && (
                    <TableCell align="right">{formatRevenueAmountPln(item)}</TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              Przychód razem: {formatBookingCurrency(data.totalRevenue)}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Margin */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Typography
          variant="body1"
          fontWeight={700}
          color={data.margin > 0 ? 'success.dark' : data.margin < 0 ? 'error.dark' : 'text.primary'}
        >
          Marża: {formatBookingCurrency(data.margin)}
        </Typography>
      </Box>
    </Box>
  );
};

export default BookingEventReportPanel;
